<template>
  <div class="form-section settings-block">
    <div class="section-content" @click.stop>
      <div v-if="loading || loadingUserData || projectDataLoading" class="muted">
        {{ __('Loading…') }}
      </div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else>
        <div v-if="context === 'user'" class="custom-report-tip" role="note">
          <p class="muted tip-text">
            {{
              s__(
                'Profile|You can open this report in the Report Constructor or request it in Telegram Direct Messages using the /my command.'
              )
            }}
          </p>
          <p class="muted tip-text tip-cta">
            <a :href="reportConstructorUrl" class="tip-link">{{
              s__('Profile|Open Report Constructor')
            }}</a>
          </p>
        </div>

        <div v-if="context === 'user'" class="report-role-filter-group">
          <div class="report-role-filter-options mobile-underlined-checklist">
            <label
              v-for="option in roleOptions"
              :key="option.value"
              class="report-role-filter-option mobile-underlined-checklist-item"
            >
              <input
                type="checkbox"
                :checked="isRoleChecked(option.value)"
                :disabled="isRoleDisabled(option.value)"
                @change="toggleRole(option.value)"
              />
              <span>{{ option.label }}</span>
            </label>
          </div>
        </div>

        <!-- Filters Panel -->
        <TaskFilterPanel
          ref="filterPanelRef"
          :members="members"
          :priorities="priorities"
          :workflow-statuses="workflowStatuses"
          :labels="labels"
          :task-categories="taskCategories"
          :initial-filters="preFilters"
          :is-personal-project="isPersonalProject"
          :show-task-type-filter="showTaskTypeFilter"
          :personal-labels-enabled="personalLabelsEnabled"
          :projects="projects"
          :hide-apply-button="true"
          :hide-assignees="context === 'user'"
          :hide-authors="context === 'user'"
          :grouped-task-categories="groupedTaskCategories"
          :grouped-workflow-statuses="groupedWorkflowStatuses"
          :grouped-members="groupedMembers"
          :grouped-labels="groupedLabels"
          :initially-expanded="true"
          :lock-expanded="true"
          @apply="handleFiltersApply"
        />

        <div class="report-settings-row">
          <!-- Ordering Panel -->
          <OrderingPanel
            v-model:order-by="localOrderBy"
            v-model:order-direction="localOrderDirection"
            :default-order-by="defaultOrderBy"
            :default-order-direction="defaultOrderDirection"
            :show-save-as-default="false"
            :show-reset="false"
            :saving="saving"
            :error="orderingError"
          />

          <!-- Swimlane Mode Panel -->
          <div class="swimlane-panel">
            <label class="ordering-label">{{ s__('Boards|Swimlanes') }}</label>
            <select v-model="localSwimlaneMode">
              <option value="none">{{ s__('Boards|None') }}</option>
              <option value="by_assignee">{{ s__('Boards|By Assignee') }}</option>
              <option value="by_author">{{ s__('Boards|By Author') }}</option>
              <option value="by_due_date">{{ s__('Boards|By Due Date') }}</option>
              <option value="by_category">{{ s__('Boards|By Category') }}</option>
              <option value="by_status">{{ s__('Boards|By Status') }}</option>
              <option value="by_priority">{{ s__('Boards|By Priority') }}</option>
              <option v-if="!hideByProjectOption" value="by_project">
                {{ s__('Boards|By Project') }}
              </option>
            </select>
          </div>
        </div>

        <!-- Save Button -->
        <div class="form-actions mobile-stretch-btn-group">
          <button
            type="button"
            :disabled="saving || !hasChanges"
            class="btn-primary"
            @click="handleSave"
          >
            <span v-if="saving">{{ s__('Profile|Saving...') }}</span>
            <span v-else>{{ s__('Profile|Save Configuration') }}</span>
          </button>
          <button type="button" :disabled="saving" class="btn-secondary" @click="handleReset">
            {{ __('Reset') }}
          </button>
        </div>

        <div v-if="saveError" class="error" style="margin-top: 12px">
          {{ saveError }}
        </div>
        <div v-if="saveSuccess" class="success" style="margin-top: 12px">
          {{ s__('Profile|Configuration saved successfully!') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __ } from '@/locale'
import TaskFilterPanel from '../tasks/TaskFilterPanel.vue'
import OrderingPanel from '../common/OrderingPanel.vue'
import '../../styles/mobile-underlined-checklist.css'

const props = defineProps({
  // Context: 'user' or 'project'
  context: { type: String, default: 'user', validator: v => ['user', 'project'].includes(v) },
  // For user context
  userId: { type: String, required: false },
  // For project context
  projectId: { type: [String, Number], required: false },
  chatId: { type: String, default: null },
  fromId: { type: String, default: null },
  // Configuration props
  title: { type: String, default: () => s__('Profile|Custom Report Configuration') },
  isPersonalProject: { type: Boolean, default: true },
  personalLabelsEnabled: { type: Boolean, default: false },
  showTaskTypeFilter: { type: Boolean, default: true },
  hideByProjectOption: { type: Boolean, default: false },
})

const loading = ref(false)
const loadingUserData = ref(false)
const error = ref('')
const saving = ref(false)
const saveError = ref('')
const saveSuccess = ref(false)
const orderingError = ref('')

// Configuration data
const reportConfiguration = ref(null)
const preFilters = ref({}) // Stored in panel format for TaskFilterPanel
const savedBoardFilters = ref({}) // Stored in board format for comparison and saving
const localOrderBy = ref('')
const localOrderDirection = ref('')
const localSwimlaneMode = ref('none')

// Default values
const defaultOrderBy = ref('created_at')
const defaultOrderDirection = ref('asc')

// Filter panel data (loaded when expanded)
const members = ref([])
const priorities = ref([])
const workflowStatuses = ref([])
const labels = ref([])
const taskCategories = ref([])
const projects = ref([])

// Grouped data for user context (grouped by project)
const groupedTaskCategories = ref(null)
const groupedWorkflowStatuses = ref(null)
const groupedMembers = ref(null) // { [projectTitle]: [members] }
const groupedLabels = ref(null) // { [projectTitle]: [labels] }

const filterPanelRef = ref(null)
const lastManualRoles = ref({
  asAuthor: true,
  asAssignee: true,
  asSubscriber: true,
})

const reportConstructorUrl = computed(() => {
  const params = new URLSearchParams()
  params.set('tab', 'reports')
  if (props.chatId) params.set('chat_id', props.chatId)
  if (props.fromId) params.set('from_id', props.fromId)
  return `/dashboard?${params.toString()}`
})

const roleOptions = computed(() => [
  { value: 'mine', label: s__('Dashboard|Mine') },
  { value: 'assigned', label: s__('Dashboard|Assigned') },
  { value: 'watching', label: s__('Dashboard|Watching') },
  { value: 'all', label: s__('Dashboard|All accessible') },
])

const selectedRolesCount = computed(() => {
  const roles = extractRoleFilters(preFilters.value)
  let count = 0
  if (roles.asAuthor) count += 1
  if (roles.asAssignee) count += 1
  if (roles.asSubscriber) count += 1
  return count
})

// GraphQL queries
const REPORT_CONFIGURATION_QUERY = gql`
  query GetReportConfiguration($projectId: ID) {
    reportConfiguration(projectId: $projectId) {
      id
      title
      preFilters
      swimlaneMode
      orderBy
      orderDirection
    }
  }
`

const PRIORITIES_QUERY = gql`
  query GetPriorities {
    priorityOptions {
      value
      label
    }
  }
`

const USER_DATA_QUERY = gql`
  query GetUserDataForReportConfig {
    currentUser {
      id
      projects(first: 100) {
        edges {
          node {
            id
            title
            archived
            labels {
              id
              title
              color
            }
            taskCategories {
              id
              name
              workflow {
                id
                name
                statuses {
                  id
                  name
                  position
                }
              }
            }
            memberships {
              id
              user {
                id
                name
                username
              }
            }
          }
        }
      }
    }
  }
`

const PROJECT_DATA_QUERY = gql`
  query ProjectDataForReportConfig($projectId: ID!) {
    project(id: $projectId) {
      id
      labels {
        id
        title
        color
      }
      members {
        id
        username
        name
      }
      taskCategories {
        id
        name
        workflow {
          id
          name
          statuses {
            id
            name
            position
          }
        }
      }
      memberships {
        id
        user {
          id
          name
          username
        }
      }
    }
    priorityOptions {
      value
      label
    }
  }
`

// Mutations
const CREATE_REPORT_CONFIGURATION_MUTATION = gql`
  mutation CreateReportConfiguration(
    $title: String
    $preFilters: JSON
    $swimlaneMode: String
    $orderBy: String
    $orderDirection: String
    $projectId: ID
  ) {
    createReportConfiguration(
      title: $title
      preFilters: $preFilters
      swimlaneMode: $swimlaneMode
      orderBy: $orderBy
      orderDirection: $orderDirection
      projectId: $projectId
    ) {
      reportConfiguration {
        id
        title
        preFilters
        swimlaneMode
        orderBy
        orderDirection
      }
      errors
    }
  }
`

const UPDATE_REPORT_CONFIGURATION_MUTATION = gql`
  mutation UpdateReportConfiguration(
    $id: ID!
    $title: String
    $preFilters: JSON
    $swimlaneMode: String
    $orderBy: String
    $orderDirection: String
  ) {
    updateReportConfiguration(
      id: $id
      title: $title
      preFilters: $preFilters
      swimlaneMode: $swimlaneMode
      orderBy: $orderBy
      orderDirection: $orderDirection
    ) {
      reportConfiguration {
        id
        title
        preFilters
        swimlaneMode
        orderBy
        orderDirection
      }
      errors
    }
  }
`

// Lazy queries
const {
  load: loadReportConfiguration,
  result: reportConfigurationResult,
  refetch: refetchReportConfiguration,
} = useLazyQuery(REPORT_CONFIGURATION_QUERY, () => ({
  projectId: props.context === 'project' && props.projectId ? String(props.projectId) : null,
}))

const { load: loadPriorities, result: prioritiesResult } = useLazyQuery(PRIORITIES_QUERY)

const {
  load: loadUserData,
  result: userDataResult,
  loading: userDataLoading,
} = useLazyQuery(USER_DATA_QUERY)

const {
  load: loadProjectData,
  result: projectDataResult,
  loading: projectDataLoading,
} = useLazyQuery(PROJECT_DATA_QUERY, () => ({
  projectId: props.projectId ? String(props.projectId) : null,
}))

// Mutations
const { mutate: createReportConfiguration } = useMutation(CREATE_REPORT_CONFIGURATION_MUTATION)
const { mutate: updateReportConfiguration } = useMutation(UPDATE_REPORT_CONFIGURATION_MUTATION)

// Convert board format filters to filter panel format
function convertBoardFiltersToPanelFormat(boardFilters) {
  const roleDefaults = getDefaultRoleFilters()
  const hasRoleKey = key => Object.prototype.hasOwnProperty.call(boardFilters, key)
  const panelFilters = {
    search: boardFilters.search || '',
    authorIds: boardFilters.authorIds ? boardFilters.authorIds.map(id => String(id)) : [],
    assigneeIds: boardFilters.assigneeIds ? boardFilters.assigneeIds.map(id => String(id)) : [],
    priorities: boardFilters.priorities
      ? boardFilters.priorities.map(id => String(id).toUpperCase())
      : [],
    workflowStatusIds: boardFilters.workflowStatusIds
      ? boardFilters.workflowStatusIds.map(id => String(id))
      : [],
    taskCategoryIds: boardFilters.taskCategoryIds
      ? boardFilters.taskCategoryIds.map(id => String(id))
      : [],
    // MultiSelect expects string IDs, so keep them as strings
    labelIds: boardFilters.labelIds ? boardFilters.labelIds.map(id => String(id)) : [],
    personalLabelIds: boardFilters.personalLabelIds
      ? boardFilters.personalLabelIds.map(id => String(id))
      : [],
    projectIds: boardFilters.projectIds ? boardFilters.projectIds.map(id => String(id)) : [],
    dueFrom: boardFilters.dueFrom || '',
    dueTo: boardFilters.dueTo || '',
    dueInDays: boardFilters.dueInDays || null,
    includeFinished:
      boardFilters.includeFinished !== undefined ? boardFilters.includeFinished : false,
    includeArchived:
      boardFilters.includeArchived !== undefined ? boardFilters.includeArchived : false,
    includeOverdue: boardFilters.includeOverdue !== undefined ? boardFilters.includeOverdue : true,
    asAuthor: hasRoleKey('asAuthor') ? boardFilters.asAuthor !== false : roleDefaults.asAuthor,
    asAssignee: hasRoleKey('asAssignee')
      ? boardFilters.asAssignee !== false
      : roleDefaults.asAssignee,
    asSubscriber: hasRoleKey('asSubscriber')
      ? boardFilters.asSubscriber !== false
      : roleDefaults.asSubscriber,
    allAccessible: hasRoleKey('allAccessible')
      ? boardFilters.allAccessible === true
      : roleDefaults.allAccessible,
  }
  return panelFilters
}

// Watch for report configuration result
watch(
  reportConfigurationResult,
  data => {
    if (data?.reportConfiguration) {
      reportConfiguration.value = data.reportConfiguration
      const boardFilters = data.reportConfiguration.preFilters || {}
      savedBoardFilters.value = { ...boardFilters }
      // Create a new object with new array references to ensure reactivity
      const panelFilters = convertBoardFiltersToPanelFormat(boardFilters)
      preFilters.value = {
        ...panelFilters,
        labelIds: [...panelFilters.labelIds],
        personalLabelIds: [...panelFilters.personalLabelIds],
        projectIds: [...panelFilters.projectIds],
      }
      updateLastManualRoles(preFilters.value)
      localOrderBy.value = data.reportConfiguration.orderBy || ''
      localOrderDirection.value = data.reportConfiguration.orderDirection || ''
      localSwimlaneMode.value = data.reportConfiguration.swimlaneMode || 'none'
      defaultOrderBy.value = data.reportConfiguration.orderBy || 'created_at'
      defaultOrderDirection.value = data.reportConfiguration.orderDirection || 'asc'
    } else {
      // No configuration exists, use defaults
      savedBoardFilters.value = {}
      preFilters.value = {
        search: '',
        labelIds: [],
        personalLabelIds: [],
        projectIds: [],
        dueInDays: null,
        includeFinished: false,
        includeArchived: false,
        includeOverdue: true,
        ...getDefaultRoleFilters(),
      }
      updateLastManualRoles(preFilters.value)
      localOrderBy.value = ''
      localOrderDirection.value = ''
      localSwimlaneMode.value = 'none'
      defaultOrderBy.value = 'created_at'
      defaultOrderDirection.value = 'asc'
    }
    loading.value = false
  },
  { immediate: true }
)

// Watch for priorities result
watch(
  prioritiesResult,
  data => {
    if (data?.priorityOptions) {
      priorities.value = data.priorityOptions
    }
  },
  { immediate: true }
)

// Watch for user/project data loading state
watch(
  [userDataLoading, projectDataLoading],
  ([userLoading, projectLoading]) => {
    loadingUserData.value = userLoading || projectLoading
  },
  { immediate: true }
)

// Helper function to process project data
function processProjectData(project) {
  const allLabels = []
  const allWorkflowStatuses = []
  const allTaskCategories = []
  const allMembers = []
  const projectsList = []

  // Collect projects for filtering (ensure IDs are strings for MultiSelect)
  if (props.context === 'user') {
    // For user context, collect from all projects
    projectsList.push({
      id: String(project.id),
      title: project.title,
    })
  }

  // Collect labels (ensure IDs are strings for MultiSelect)
  if (project.labels && project.labels.length > 0) {
    project.labels.forEach(label => {
      if (!allLabels.find(l => l.id === String(label.id))) {
        allLabels.push({
          id: String(label.id),
          title: label.title,
          color: label.color,
        })
      }
    })
  }

  // Collect task categories
  if (project.taskCategories && project.taskCategories.length > 0) {
    project.taskCategories.forEach(category => {
      if (!allTaskCategories.find(c => c.id === category.id)) {
        allTaskCategories.push({
          id: category.id,
          name: category.name,
        })
      }
    })
  }

  // Collect workflow statuses
  if (project.taskCategories && project.taskCategories.length > 0) {
    project.taskCategories.forEach(category => {
      if (category.workflow && category.workflow.statuses) {
        category.workflow.statuses.forEach(status => {
          if (!allWorkflowStatuses.find(s => s.id === status.id)) {
            allWorkflowStatuses.push({
              id: status.id,
              name: status.name,
              position: status.position,
              color: status.color,
            })
          }
        })
      }
    })
  }

  // Collect members
  if (project.memberships && project.memberships.length > 0) {
    project.memberships.forEach(membership => {
      if (membership.user && !allMembers.find(m => m.id === membership.user.id)) {
        allMembers.push({
          id: membership.user.id,
          name: membership.user.name,
          username: membership.user.username,
        })
      }
    })
  }

  return { allLabels, allWorkflowStatuses, allTaskCategories, allMembers, projectsList }
}

// Watch for user data result (projects, labels, workflow statuses, task categories, members)
watch(
  userDataResult,
  data => {
    if (data?.currentUser?.projects?.edges) {
      let allLabels = []
      let allWorkflowStatuses = []
      let allTaskCategories = []
      let allMembers = []
      let projectsList = []

      // Grouped data structures for user context
      const taskCategoriesByProject = {}
      const workflowStatusesByProject = {}
      const membersByProject = {}
      const labelsByProject = {}
      const groupedStatuses = {}

      data.currentUser.projects.edges.forEach(edge => {
        const project = edge.node
        // Skip archived projects
        if (project.archived) {
          return
        }

        const processed = processProjectData(project)
        allLabels = [...allLabels, ...processed.allLabels]
        allWorkflowStatuses = [...allWorkflowStatuses, ...processed.allWorkflowStatuses]
        allTaskCategories = [...allTaskCategories, ...processed.allTaskCategories]
        allMembers = [...allMembers, ...processed.allMembers]
        projectsList = [...projectsList, ...processed.projectsList]

        // Group by project for user context
        if (props.context === 'user') {
          // Group task categories by project
          if (project.taskCategories && project.taskCategories.length > 0) {
            taskCategoriesByProject[project.title] = project.taskCategories.map(cat => ({
              id: cat.id,
              name: cat.name,
            }))
          }

          // Group workflow statuses by project
          if (project.taskCategories && project.taskCategories.length > 0) {
            project.taskCategories.forEach(category => {
              if (category.workflow && category.workflow.statuses) {
                const workflowKey = `${project.id}_${category.workflow.id}`
                if (!groupedStatuses[workflowKey]) {
                  groupedStatuses[workflowKey] = {
                    project: {
                      id: project.id,
                      title: project.title,
                    },
                    workflow: {
                      id: category.workflow.id,
                      name: category.workflow.name,
                    },
                    taskCategory: {
                      id: category.id,
                      name: category.name,
                    },
                    statuses: [],
                  }
                }
                category.workflow.statuses.forEach(status => {
                  if (!groupedStatuses[workflowKey].statuses.find(s => s.id === status.id)) {
                    groupedStatuses[workflowKey].statuses.push({
                      id: status.id,
                      name: status.name,
                      position: status.position,
                      color: status.color,
                    })
                  }
                })
              }
            })
          }

          // Group members by project
          if (project.memberships && project.memberships.length > 0) {
            membersByProject[project.title] = project.memberships
              .map(m => m.user)
              .filter(user => user)
              .map(user => ({
                id: user.id,
                name: user.name,
                username: user.username,
              }))
          }

          // Group labels by project
          if (project.labels && project.labels.length > 0) {
            labelsByProject[project.title] = project.labels.map(label => ({
              id: String(label.id),
              title: label.title,
              color: label.color,
            }))
          }
        }
      })

      // Sort statuses within each workflow
      Object.keys(groupedStatuses).forEach(key => {
        groupedStatuses[key].statuses.sort((a, b) => a.position - b.position)
      })

      projects.value = projectsList
      labels.value = allLabels
      taskCategories.value = allTaskCategories
      workflowStatuses.value = allWorkflowStatuses.sort((a, b) => a.position - b.position)
      members.value = allMembers

      // Set grouped data for user context
      if (props.context === 'user') {
        groupedTaskCategories.value =
          Object.keys(taskCategoriesByProject).length > 0 ? taskCategoriesByProject : null
        groupedWorkflowStatuses.value =
          Object.keys(groupedStatuses).length > 0 ? groupedStatuses : null
        groupedMembers.value = Object.keys(membersByProject).length > 0 ? membersByProject : null
        groupedLabels.value = Object.keys(labelsByProject).length > 0 ? labelsByProject : null
      } else {
        groupedTaskCategories.value = null
        groupedWorkflowStatuses.value = null
        groupedMembers.value = null
        groupedLabels.value = null
      }
    }
  },
  { immediate: true }
)

// Watch for project data result (for project context)
watch(
  projectDataResult,
  data => {
    if (data?.project) {
      const processed = processProjectData(data.project)
      labels.value = processed.allLabels
      taskCategories.value = processed.allTaskCategories
      workflowStatuses.value = processed.allWorkflowStatuses.sort((a, b) => a.position - b.position)
      members.value = processed.allMembers
      projects.value = [] // No projects filter for project-level reports
    }
  },
  { immediate: true }
)

// Computed
const currentSwimlaneDisplay = computed(() => {
  const swimlaneLabels = {
    none: s__('Boards|None'),
    by_assignee: s__('Boards|By Assignee'),
    by_author: s__('Boards|By Author'),
    by_due_date: s__('Boards|By Due Date'),
    by_category: s__('Boards|By Category'),
    by_status: s__('Boards|By Status'),
    by_priority: s__('Boards|By Priority'),
    by_project: s__('Boards|By Project'),
  }
  return swimlaneLabels[localSwimlaneMode.value] || localSwimlaneMode.value
})

// Helper function to normalize filters for comparison
function normalizeFiltersForComparison(filters) {
  const normalized = { ...filters }
  const hasFilterKey = key => Object.prototype.hasOwnProperty.call(normalized, key)
  // Sort arrays to ensure consistent comparison
  if (normalized.workflowStatusIds) {
    normalized.workflowStatusIds = [...normalized.workflowStatusIds].sort()
  }
  if (normalized.priorities) {
    // Normalize priorities to lowercase for comparison (saved format)
    normalized.priorities = [...normalized.priorities].map(p => String(p).toLowerCase()).sort()
  }
  if (normalized.labelIds) {
    normalized.labelIds = [...normalized.labelIds].sort()
  }
  if (normalized.personalLabelIds) {
    normalized.personalLabelIds = [...normalized.personalLabelIds].sort()
  }
  if (normalized.projectIds) {
    normalized.projectIds = [...normalized.projectIds].sort()
  }
  if (normalized.authorIds) {
    normalized.authorIds = [...normalized.authorIds].sort()
  }
  if (normalized.assigneeIds) {
    normalized.assigneeIds = [...normalized.assigneeIds].sort()
  }
  if (normalized.taskCategoryIds) {
    normalized.taskCategoryIds = [...normalized.taskCategoryIds].sort()
  }
  if (props.context === 'user') {
    normalized.asAuthor = hasFilterKey('asAuthor') ? normalized.asAuthor !== false : true
    normalized.asAssignee = hasFilterKey('asAssignee') ? normalized.asAssignee !== false : true
    normalized.asSubscriber = hasFilterKey('asSubscriber')
      ? normalized.asSubscriber !== false
      : true
    normalized.allAccessible = hasFilterKey('allAccessible')
      ? normalized.allAccessible === true
      : false
  }
  return normalized
}

const hasChanges = computed(() => {
  // Convert current panel filters to board format for comparison
  const currentBoardFilters = convertFiltersToBoardFormat(preFilters.value)
  const normalizedCurrent = normalizeFiltersForComparison(currentBoardFilters)
  const normalizedSaved = normalizeFiltersForComparison(savedBoardFilters.value)

  if (!reportConfiguration.value) {
    // New configuration - check if any values differ from defaults
    const hasFilters = Object.keys(currentBoardFilters).length > 0
    return (
      hasFilters ||
      localOrderBy.value !== '' ||
      localOrderDirection.value !== '' ||
      localSwimlaneMode.value !== 'none'
    )
  }

  // Existing configuration - compare with saved values
  const saved = reportConfiguration.value

  return (
    JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedSaved) ||
    (localOrderBy.value || defaultOrderBy.value) !== (saved.orderBy || 'created_at') ||
    (localOrderDirection.value || defaultOrderDirection.value) !==
      (saved.orderDirection || 'asc') ||
    localSwimlaneMode.value !== (saved.swimlaneMode || 'none')
  )
})

// Functions
function handleFiltersApply(filters) {
  // Only update preFilters if values actually changed to prevent infinite loops
  // Compare key values instead of JSON.stringify to handle arrays and undefined properly
  const current = preFilters.value
  const newFilters = filters

  const filtersChanged =
    (current.search || '') !== (newFilters.search || '') ||
    JSON.stringify(current.authorIds || []) !== JSON.stringify(newFilters.authorIds || []) ||
    JSON.stringify(current.assigneeIds || []) !== JSON.stringify(newFilters.assigneeIds || []) ||
    JSON.stringify(current.priorities || []) !== JSON.stringify(newFilters.priorities || []) ||
    JSON.stringify(current.workflowStatusIds || []) !==
      JSON.stringify(newFilters.workflowStatusIds || []) ||
    JSON.stringify(current.taskCategoryIds || []) !==
      JSON.stringify(newFilters.taskCategoryIds || []) ||
    JSON.stringify(current.labelIds || []) !== JSON.stringify(newFilters.labelIds || []) ||
    JSON.stringify(current.personalLabelIds || []) !==
      JSON.stringify(newFilters.personalLabelIds || []) ||
    JSON.stringify(current.projectIds || []) !== JSON.stringify(newFilters.projectIds || []) ||
    (current.dueFrom || '') !== (newFilters.dueFrom || '') ||
    (current.dueTo || '') !== (newFilters.dueTo || '') ||
    (current.dueInDays || null) !== (newFilters.dueInDays || null) ||
    (current.includeFinished || false) !== (newFilters.includeFinished || false) ||
    (current.includeArchived || false) !== (newFilters.includeArchived || false) ||
    (current.includeOverdue !== undefined ? current.includeOverdue : true) !==
      (newFilters.includeOverdue !== undefined ? newFilters.includeOverdue : true)

  if (filtersChanged) {
    // Update preFilters in panel format (for TaskFilterPanel)
    preFilters.value = { ...filters, ...extractRoleFilters(current) }
  }
}

function convertFiltersToBoardFormat(filters) {
  const boardFilters = {}
  if (filters.search && filters.search.trim().length >= 3) {
    boardFilters.search = filters.search.trim()
  }
  if (filters.labelIds && filters.labelIds.length > 0) {
    boardFilters.labelIds = filters.labelIds.map(id => String(id))
  }
  if (filters.personalLabelIds && filters.personalLabelIds.length > 0) {
    boardFilters.personalLabelIds = filters.personalLabelIds.map(id => String(id))
  }
  if (filters.projectIds && filters.projectIds.length > 0) {
    boardFilters.projectIds = filters.projectIds.map(id => String(id))
  }
  if (filters.authorIds && filters.authorIds.length > 0) {
    boardFilters.authorIds = filters.authorIds.map(id => String(id))
  }
  if (filters.assigneeIds && filters.assigneeIds.length > 0) {
    boardFilters.assigneeIds = filters.assigneeIds.map(id => String(id))
  }
  if (filters.priorities && filters.priorities.length > 0) {
    boardFilters.priorities = filters.priorities.map(p => String(p).toLowerCase())
  }
  if (filters.workflowStatusIds && filters.workflowStatusIds.length > 0) {
    boardFilters.workflowStatusIds = filters.workflowStatusIds.map(id => String(id))
  }
  if (filters.taskCategoryIds && filters.taskCategoryIds.length > 0) {
    boardFilters.taskCategoryIds = filters.taskCategoryIds.map(id => String(id))
  }
  if (filters.dueFrom) {
    boardFilters.dueFrom = filters.dueFrom
  }
  if (filters.dueTo) {
    boardFilters.dueTo = filters.dueTo
  }
  if (filters.includeFinished !== undefined) {
    boardFilters.includeFinished = filters.includeFinished
  }
  if (filters.includeArchived !== undefined) {
    boardFilters.includeArchived = filters.includeArchived
  }
  if (filters.dueInDays && filters.dueInDays > 0) {
    boardFilters.dueInDays = filters.dueInDays
  }
  if (filters.includeOverdue !== undefined) {
    boardFilters.includeOverdue = filters.includeOverdue
  }
  if (props.context === 'user') {
    const roleFilters = extractRoleFilters(filters)
    boardFilters.asAuthor = roleFilters.asAuthor
    boardFilters.asAssignee = roleFilters.asAssignee
    boardFilters.asSubscriber = roleFilters.asSubscriber
    boardFilters.allAccessible = roleFilters.allAccessible
  }
  return boardFilters
}

function getDefaultRoleFilters() {
  return {
    asAuthor: true,
    asAssignee: true,
    asSubscriber: true,
    allAccessible: false,
  }
}

function extractRoleFilters(filters = {}) {
  const defaults = getDefaultRoleFilters()
  return {
    asAuthor: filters.asAuthor !== false,
    asAssignee: filters.asAssignee !== false,
    asSubscriber: filters.asSubscriber !== false,
    allAccessible: filters.allAccessible === true || defaults.allAccessible,
  }
}

function updateLastManualRoles(filters) {
  const roles = extractRoleFilters(filters)
  if (roles.allAccessible) return
  lastManualRoles.value = {
    asAuthor: roles.asAuthor,
    asAssignee: roles.asAssignee,
    asSubscriber: roles.asSubscriber,
  }
}

function isRoleChecked(roleName) {
  const roles = extractRoleFilters(preFilters.value)
  if (roleName === 'all') return roles.allAccessible
  if (roleName === 'mine') return roles.asAuthor
  if (roleName === 'assigned') return roles.asAssignee
  if (roleName === 'watching') return roles.asSubscriber
  return false
}

function isRoleDisabled(roleName) {
  if (roleName === 'all') return false
  const roles = extractRoleFilters(preFilters.value)
  if (roles.allAccessible) return true

  const isChecked = isRoleChecked(roleName)
  return isChecked && selectedRolesCount.value === 1
}

function toggleRole(roleName) {
  if (props.context !== 'user') return

  const currentRoles = extractRoleFilters(preFilters.value)
  if (roleName === 'all') {
    if (!currentRoles.allAccessible) {
      updateLastManualRoles(preFilters.value)
      preFilters.value = {
        ...preFilters.value,
        asAuthor: true,
        asAssignee: true,
        asSubscriber: true,
        allAccessible: true,
      }
      return
    }

    const restore = {
      asAuthor: lastManualRoles.value.asAuthor,
      asAssignee: lastManualRoles.value.asAssignee,
      asSubscriber: lastManualRoles.value.asSubscriber,
    }
    if (!restore.asAuthor && !restore.asAssignee && !restore.asSubscriber) {
      restore.asAuthor = true
    }

    preFilters.value = {
      ...preFilters.value,
      ...restore,
      allAccessible: false,
    }
    updateLastManualRoles(preFilters.value)
    return
  }

  if (currentRoles.allAccessible) return

  const next = {
    asAuthor: currentRoles.asAuthor,
    asAssignee: currentRoles.asAssignee,
    asSubscriber: currentRoles.asSubscriber,
  }

  if (roleName === 'mine') next.asAuthor = !next.asAuthor
  if (roleName === 'assigned') next.asAssignee = !next.asAssignee
  if (roleName === 'watching') next.asSubscriber = !next.asSubscriber

  if (!next.asAuthor && !next.asAssignee && !next.asSubscriber) {
    next.asAuthor = true
  }

  preFilters.value = {
    ...preFilters.value,
    ...next,
    allAccessible: false,
  }
  updateLastManualRoles(preFilters.value)
}

async function handleSave() {
  if (saving.value) return

  saving.value = true
  saveError.value = ''
  saveSuccess.value = false
  orderingError.value = ''

  try {
    // Convert current panel filters to board format
    const filters = convertFiltersToBoardFormat(preFilters.value)

    const orderBy = localOrderBy.value || defaultOrderBy.value
    const orderDirection = localOrderDirection.value || defaultOrderDirection.value

    let mutationResult
    if (reportConfiguration.value) {
      // Update existing configuration
      mutationResult = await updateReportConfiguration({
        id: reportConfiguration.value.id,
        preFilters: filters,
        swimlaneMode: localSwimlaneMode.value,
        orderBy: orderBy,
        orderDirection: orderDirection,
      })
    } else {
      // Create new configuration
      mutationResult = await createReportConfiguration({
        title: 'Default',
        preFilters: filters,
        swimlaneMode: localSwimlaneMode.value,
        orderBy: orderBy,
        orderDirection: orderDirection,
        projectId: props.context === 'project' ? props.projectId : null,
      })
    }

    const createPayload =
      mutationResult?.data?.createReportConfiguration || mutationResult?.createReportConfiguration
    const updatePayload =
      mutationResult?.data?.updateReportConfiguration || mutationResult?.updateReportConfiguration
    const payload = createPayload || updatePayload

    if (payload?.errors?.length) {
      saveError.value = payload.errors.join(', ')
    } else {
      if (payload?.reportConfiguration) {
        reportConfiguration.value = payload.reportConfiguration
      }
      // Update saved board filters
      savedBoardFilters.value = { ...filters }
      // Reload configuration to get updated data
      if (reportConfiguration.value?.id && refetchReportConfiguration) {
        await refetchReportConfiguration()
      } else {
        await loadReportConfiguration()
      }
      saveSuccess.value = true
    }
  } catch (err) {
    saveError.value = err.message || s__('Profile|Failed to save configuration')
  } finally {
    saving.value = false
  }
}

function handleReset() {
  if (reportConfiguration.value) {
    // Reset to saved values
    const boardFilters = savedBoardFilters.value
    preFilters.value = convertBoardFiltersToPanelFormat(boardFilters)
    localOrderBy.value = reportConfiguration.value.orderBy || ''
    localOrderDirection.value = reportConfiguration.value.orderDirection || ''
    localSwimlaneMode.value = reportConfiguration.value.swimlaneMode || 'none'
  } else {
    // Reset to defaults
    savedBoardFilters.value = {}
    preFilters.value = { ...getDefaultRoleFilters() }
    updateLastManualRoles(preFilters.value)
    localOrderBy.value = ''
    localOrderDirection.value = ''
    localSwimlaneMode.value = 'none'
  }

  // Reset filter panel
  if (filterPanelRef.value) {
    filterPanelRef.value.resetFilters()
  }

  saveError.value = ''
  saveSuccess.value = false
}

// Load data immediately on mount
onMounted(() => {
  if (!reportConfigurationResult.value) {
    loading.value = true
    error.value = ''
    loadReportConfiguration()
    loadPriorities()
    if (props.context === 'project') {
      loadProjectData()
    } else {
      loadUserData()
    }
  }
})

// Expose methods for parent components (e.g., setup guides)
defineExpose({
  hasChanges: () => hasChanges.value, // Expose as function that returns computed value
  handleSave,
  handleReset,
})
</script>

<style scoped>
.active-count {
  color: #1d6bf3;
  font-weight: 700;
}

.report-settings-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin: 10px 0 16px;
}

.report-settings-row .ordering-wrapper {
  margin-bottom: 0;
}

.ordering-label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #495057;
}

.swimlane-panel {
  margin-bottom: 0;
}

@media (min-width: 1024px) {
  .report-settings-row {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

/* Button styles inherit from forms.css */

.error {
  color: #d32f2f;
  font-size: 14px;
}

.success {
  color: #2e7d32;
  font-size: 14px;
}

.muted {
  color: #999;
  font-size: 14px;
}

.custom-report-tip {
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.tip-text {
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.45;
}

.tip-cta {
  margin-top: 8px;
}

.tip-link {
  color: #2aabee;
  font-weight: 600;
  text-decoration: none;
}

.tip-link:hover {
  text-decoration: underline;
}

.report-role-filter-group {
  margin: -6px 0 14px;
}

.report-role-filter-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.report-role-filter-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 999px;
  background: #fff;
  color: #333;
  font-size: 13px;
}

.report-role-filter-option input[type='checkbox'] {
  margin: 0;
}
</style>
